import type Phaser from 'phaser';
import { avatarIcon } from '../config/avatars';

/**
 * Draw an avatar icon, fitted whole inside a box.
 *
 * Every site that shows an icon goes through here — the leaderboard row, the
 * picker cell and the Avatar Shop slot — so a new icon cannot end up drawn
 * three different ways.
 *
 * Two things this does that a bare `setDisplaySize(n, n)` cannot:
 *
 * - **It respects the frame's aspect ratio.** The free icons are all square
 *   sprites, but The Hambi Generic is the title logo — a wide banner. Squashed
 *   into a square it reads as a smear; scaled by the smaller of the two ratios
 *   it stays legible, a band across the middle of the circle. (The user chose
 *   fitting the whole logo over cropping the circle full of it.)
 * - **It follows `texture`, not `key`.** A shop icon stores `icon-<code>` and
 *   names its art separately, because Upside-Downy shares a frame with the
 *   free Hambi icon and The Shrekbook's art is tier 3.
 *
 * Returns null when there is nothing to draw — an unknown key, or art missing
 * from the build — so callers keep their empty-circle fallback.
 *
 * Spec: docs/superpowers/specs/2026-09-21-avatar-shop-design.md §2
 */
export function drawAvatarIcon(
  scene: Phaser.Scene,
  key: string,
  boxW: number,
  boxH: number,
): Phaser.GameObjects.Image | null {
  if (!key) return null;
  const icon = avatarIcon(key);
  // An unrecognised key still renders if a texture happens to match it: that
  // keeps a row written by a newer client from becoming an empty circle.
  const texture = icon?.texture ?? key;
  if (!scene.textures.exists(texture)) return null;

  const img = scene.add.image(0, 0, texture);
  const scale = Math.min(boxW / img.width, boxH / img.height);
  img.setScale(scale);
  if (icon?.flipY) img.setFlipY(true);
  return img;
}
